import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import EcommerceNav from '../../components/ecommerce/EcommerceNav';
import EcommerceFooter from '../../components/ecommerce/EcommerceFooter';
import './Ecommerce.css';
import './ProductList.css';
import { formatINR } from '../../utils/currency';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    minPrice: '',
    maxPrice: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchProducts();
  }, [searchParams]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(searchParams);
      const response = await api.get(`/api/ecommerce/products?${params.toString()}`);
      
      if (response.data.success) {
        setProducts(response.data.data.products);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      minPrice: '',
      maxPrice: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    setSearchParams({});
  };

  const formatPrice = (price) => formatINR(price);

  if (loading) {
    return (
      <div className="product-list-container">
        <div className="loading">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="ecommerce-page">
      <EcommerceNav />
      <div className="product-list-container">
        <div className="product-list-header">
        <h1>Our Products</h1>
        <p>Discover our amazing collection of products</p>
      </div>

      <div className="product-list-content">

        {/* Products Grid */}
        <div className="products-section">
          {products.length === 0 ? (
            <div className="no-products">
              <h3>No products found</h3>
              <p>Try adjusting your filters or search terms</p>
            </div>
          ) : (
            <>
              <div className="products-header">
                <p>
                  Showing {products.length} of {pagination.totalProducts} products
                </p>
              </div>

              <div className="products-grid">
                {products.map((product) => (
                  <div key={product._id} className="product-card">
                    <Link to={`/ecommerce/products/${product.slug}`} className="product-link">
                      <div className="product-image">
                        {product.inventory.images && product.inventory.images.length > 0 ? (
                          <img
                            src={product.inventory.images[0]}
                            alt={product.inventory.itemName}
                            onError={(e) => {
                              e.target.src = '/placeholder-product.jpg';
                            }}
                          />
                        ) : (
                          <div className="placeholder-image">
                            <span>No Image</span>
                          </div>
                        )}
                        {product.isFeatured && (
                          <div className="featured-badge">Featured</div>
                        )}
                      </div>
                      
                      <div className="product-info">
                        <h3 className="product-name">{product.inventory.itemName}</h3>
                        <p className="product-category">{product.inventory.category}</p>
                        
                        <div className="product-price">
                          {product.inventory.discountedPrice ? (
                            <>
                              <span className="current-price">
                                {formatPrice(product.inventory.discountedPrice)}
                              </span>
                              <span className="original-price">
                                {formatPrice(product.inventory.sellingPrice)}
                              </span>
                            </>
                          ) : (
                            <span className="current-price">
                              {formatPrice(product.inventory.sellingPrice)}
                            </span>
                          )}
                        </div>

                        <div className="product-rating">
                          <div className="stars">
                            {[...Array(5)].map((_, i) => (
                              <span
                                key={i}
                                className={`star ${
                                  i < Math.floor(product.averageRating) ? 'filled' : ''
                                }`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="rating-text">
                            ({product.reviewCount} reviews)
                          </span>
                        </div>

                        <div className="product-stock">
                          {product.inventory.itemQuantity > 0 ? (
                            <span className="in-stock">
                              {product.inventory.itemQuantity} in stock
                            </span>
                          ) : (
                            <span className="out-of-stock">Out of stock</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => {
                      const params = new URLSearchParams(searchParams);
                      params.set('page', pagination.currentPage - 1);
                      setSearchParams(params);
                    }}
                    disabled={!pagination.hasPrev}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  
                  <span className="pagination-info">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  
                  <button
                    onClick={() => {
                      const params = new URLSearchParams(searchParams);
                      params.set('page', pagination.currentPage + 1);
                      setSearchParams(params);
                    }}
                    disabled={!pagination.hasNext}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div>
      <EcommerceFooter />
    </div>
  );
};

export default ProductList;
